import { FC, memo } from "react";

import { Button } from "@/components/_atoms/button";
import { OwnerTypes } from "@/domain/_shared/lib/owner-types";
import { FocusAreaSelectDropdown } from "@/domain/focus-area/components/focus-area-select-dropdown";
import { FocusAreaViewOptional } from "@/domain/focus-area/components/focus-area-view-optional";
import { useCreateFocusAreaInstance } from "@/domain/focus-area/hooks/api/use-create-focus-area-instance";
import { useDeleteFocusAreaInstance } from "@/domain/focus-area/hooks/api/use-delete-focus-area-instance";
import { useUpdateFocusAreaInstance } from "@/domain/focus-area/hooks/api/use-update-focus-area-instance";
import { useDatatableContext } from "@/domain/view/components/datatable/use-datatable-context";
import { cn } from "@/lib/cn";

import { FocusAreaInstance } from "../../models/focus-area-model";

interface DatatableFocusAreaCellProps {
  focusAreaInstance: FocusAreaInstance | null;
  workspaceId: string;
  ownerId: string;
  editable: boolean;
  className?: string;
}

interface InternalDatatableFocusAreaCellProps extends DatatableFocusAreaCellProps {
  showColumnHeaders: boolean;
}

const InternalDatatableFocusAreaCell: FC<InternalDatatableFocusAreaCellProps> = ({
  focusAreaInstance,
  workspaceId,
  ownerId,
  editable,
}) => {
  const { updateFocusAreaInstance } = useUpdateFocusAreaInstance();
  const { createFocusAreaInstance } = useCreateFocusAreaInstance();
  const { deleteFocusAreaInstance } = useDeleteFocusAreaInstance();

  const handleOnSelect = (focusArea: { id: string } | null) => {
    if (!focusArea) {
      if (focusAreaInstance) {
        deleteFocusAreaInstance({ id: focusAreaInstance.id });
      }
      return;
    }

    if (focusAreaInstance) {
      updateFocusAreaInstance({
        id: focusAreaInstance.id,
        focusAreaId: focusArea.id,
      });
    } else {
      createFocusAreaInstance({
        focusAreaId: focusArea.id,
        ownerId: ownerId,
        ownerType: OwnerTypes.CLIMATE_ACTION,
      });
    }
  };

  if (!editable) {
    return (
      <div className="inline-flex h-7 items-center justify-center whitespace-nowrap rounded-md px-2 text-xs font-normal">
        <FocusAreaViewOptional
          focusArea={focusAreaInstance?.focusArea ?? null}
          logoOnly={false}
          className={cn(["xs h-3 w-3.5", focusAreaInstance ? "" : "text-muted-foreground"])}
        />
      </div>
    );
  }

  return (
    <FocusAreaSelectDropdown
      workspaceId={workspaceId}
      onSelect={handleOnSelect}
      selected={focusAreaInstance}
      side="bottom"
      asChild
      data-testid="datatable-focusArea-cell"
    >
      <Button
        variant="ghost"
        size="xs"
        className={cn([
          "overflow-x-hidden text-xs font-normal ring-offset-background focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus",
        ])}
      >
        <FocusAreaViewOptional
          focusArea={focusAreaInstance?.focusArea ?? null}
          logoOnly={false}
          className={cn(["xs h-3 w-3.5", focusAreaInstance ? "" : "text-muted-foreground"])}
        />
      </Button>
    </FocusAreaSelectDropdown>
  );
};

const MemoizedInternalDatatableFocusAreaCell = memo(InternalDatatableFocusAreaCell, (prevProps, nextProps) => {
  return (
    prevProps.focusAreaInstance?.id === nextProps.focusAreaInstance?.id &&
    prevProps.focusAreaInstance?.focusAreaId === nextProps.focusAreaInstance?.focusAreaId &&
    prevProps.workspaceId === nextProps.workspaceId &&
    prevProps.ownerId === nextProps.ownerId &&
    prevProps.editable === nextProps.editable &&
    prevProps.showColumnHeaders === nextProps.showColumnHeaders
  );
});

export const DatatableFocusAreaCell: FC<DatatableFocusAreaCellProps> = (props) => {
  const { state } = useDatatableContext();
  const showColumnHeaders = state.showColumnHeaders;

  return <MemoizedInternalDatatableFocusAreaCell {...props} showColumnHeaders={showColumnHeaders} />;
};
